import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useFragment } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { Button } from "@whoosh/design-system/src/components/controls";
import { useModalTransition } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import { EditBookingModal } from "../../../EditBooking/EditBookingModal";
import { bookingSummaryAnalyticsDataAtom } from "../../atoms/analytics/bookingSummaryAnalyticsDataAtom";
import { useApplyPromoCodeToBooking } from "../BookingCredits/components/BookingPromoCode/hooks/useApplyPromoCodeToBooking";
import { useShouldPresentBookingAsLockedRequest } from "../BookingRequest/components/AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import { EditBookingButton_Booking$key } from "./__generated__/EditBookingButton_Booking.graphql";

type Props = {
  bookingRef?: EditBookingButton_Booking$key | null;
  currentSessionMemberShipTypeId: string;
  currentSessionUserClientId: string;
};
export const EditBookingButton = ({
  bookingRef,
  currentSessionMemberShipTypeId,
  currentSessionUserClientId,
}: Props) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPromoCodeWarningOpen, setIsPromoCodeWarningOpen] = useState(false);
  const [promoCodeRemovalError, setPromoCodeRemovalError] = useState<
    string | null
  >(null);
  const [applyPromoCodeToBooking, isRemovingPromoCode] =
    useApplyPromoCodeToBooking();
  const { runAfterIdle } = useModalTransition();
  const booking = useFragment(EditBookingButtonFragmentString, bookingRef);
  const canUpdate = booking?.canUpdate;
  const requiresPrePayment =
    booking?.paymentRequirements?.prePaymentRequirement === "FULL";

  const bookingLockedDueToPrePaymentAndStaffCreated =
    booking?.source === "STAFF" && requiresPrePayment;

  const isQueueSlot = booking?.startSlot?.__typename === "QueueSlot";
  const shouldPresentAsRequest = useShouldPresentBookingAsLockedRequest(
    booking ?? null
  );
  const isRequestPresentation = isQueueSlot || shouldPresentAsRequest;

  const shouldShowEditBookingButton =
    booking &&
    canUpdate &&
    (isRequestPresentation || !bookingLockedDueToPrePaymentAndStaffCreated);

  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  return shouldShowEditBookingButton ? (
    <VStack>
      <Button
        accessibilityLabel={t`Edit Booking button`}
        size="small"
        variant="secondary"
        onPress={() => {
          trackEvent("BookingDetail.EditBookingClicked", analyticsData ?? {});
          if (booking.appliedPromoCode) {
            setPromoCodeRemovalError(null);
            setIsPromoCodeWarningOpen(true);
          } else {
            setIsEditOpen(true);
          }
        }}
      >
        {isRequestPresentation ? t`Edit Request` : t`Edit Booking`}
      </Button>
      <EditBookingModal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
        }}
        bookingRef={booking}
        currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
        currentSessionUserClientId={currentSessionUserClientId}
      />
      <PopUpModal
        isOpen={isPromoCodeWarningOpen}
        upperTitle={t`Edit Booking`}
        cancelText={t`Cancel`}
        confirmText={t`Edit Booking`}
        isConfirmDisabled={isRemovingPromoCode}
        onClose={(confirm: boolean) => {
          if (isRemovingPromoCode) {
            return;
          }

          if (confirm) {
            setPromoCodeRemovalError(null);
            applyPromoCodeToBooking(
              { bookingId: booking.id, promoCode: null },
              () => {
                setIsPromoCodeWarningOpen(false);
                runAfterIdle(() => {
                  setIsEditOpen(true);
                });
              },
              setPromoCodeRemovalError
            );
          } else {
            setPromoCodeRemovalError(null);
            setIsPromoCodeWarningOpen(false);
          }
        }}
        content={
          <VStack gap={4}>
            <Text color="text-secondary">
              {t`Your applied promo code will be removed if you edit this booking.`}
            </Text>
            <Text color="text-secondary">
              {t`You can add a promo code again after making your changes.`}
            </Text>
            {promoCodeRemovalError ? (
              <Text color="text-error" testID="promo-code-removal-error">
                {promoCodeRemovalError}
              </Text>
            ) : null}
          </VStack>
        }
      />
    </VStack>
  ) : null;
};

const EditBookingButtonFragmentString = graphql`
  fragment EditBookingButton_Booking on Booking {
    paymentRequirements {
      prePaymentRequirement
    }
    id
    __typename
    canUpdate
    source
    appliedPromoCode {
      id
    }
    ...useShouldPresentBookingAsLockedRequest_booking
    ...EditBookingModal_Booking
    startSlot {
      __typename
    }
  }
`;
